import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, KECAMATAN_MAGELANG, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

const RULES = {
  name: (v) => !v?.trim() ? "Nama wajib diisi" : v.trim().length < 2 ? "Nama minimal 2 karakter" : "",
  username: (v) => {
    if (!v?.trim()) return "";
    const u = v.trim().toLowerCase();
    return /^[a-z0-9_.]{3,30}$/.test(u) ? "" : "Username 3-30 karakter (huruf kecil, angka, . _)";
  },
  email: (v) => !v?.trim() ? "Email wajib diisi" : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? "" : "Format email tidak valid",
  password: (v) => !v ? "Password wajib diisi" : v.length < 8 ? "Password minimal 8 karakter" : "",
  confirm: (v, all) => v !== all.password ? "Konfirmasi password tidak cocok" : "",
  whatsapp: (v) => {
    if (!v?.trim()) return "Nomor WhatsApp wajib diisi";
    const raw = v.trim();
    if (/[^\d+\s-]/.test(raw)) return "Hanya boleh angka (tanda + di awal)";
    const d = raw.replace(/\D/g, "");
    if (d.length < 9 || d.length > 15) return "Nomor tidak valid (9-15 digit)";
    return "";
  },
  address: (v) => !v?.trim() ? "Alamat wajib diisi" : "",
  photo_path: (v) => !v ? "Foto profil wajib diunggah" : "",
  business_name: (v) => !v?.trim() ? "Nama usaha wajib diisi" : "",
  business_address: (v) => !v?.trim() ? "Alamat usaha wajib diisi" : "",
  business_description: (v) => !v?.trim() ? "Deskripsi usaha wajib diisi" : "",
  business_photo_path: (v) => !v ? "Foto usaha wajib diunggah" : "",
};

const validateAll = (form) => {
  const errs = {};
  Object.keys(RULES).forEach(k => {
    const msg = RULES[k](form[k], form);
    if (msg) errs[k] = msg;
  });
  return errs;
};

export default function Register() {
  const nav = useNavigate();
  const { refresh, user, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", username: "", email: "", password: "", confirm: "",
    whatsapp: "", address: "", kecamatan: KECAMATAN_MAGELANG[0], kabupaten: "Magelang",
    photo_path: "",
    business_name: "", business_address: "", business_photo_path: "", business_description: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => { if (!loading && user) nav("/app", { replace: true }); }, [user, loading, nav]);

  const setField = (k, v) => {
    const next = { ...form, [k]: v };
    setForm(next);
    if (RULES[k]) setErrors(prev => ({ ...prev, [k]: RULES[k](v, next) }));
    if (k === "password") setErrors(prev => ({ ...prev, confirm: RULES.confirm(next.confirm, next) }));
  };
  const blur = (k) => setTouched(prev => ({ ...prev, [k]: true }));
  const err = (k) => (touched[k] || errors[k]) && errors[k];

  const uploadPublic = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/upload-public", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const submit = async (e) => {
    e.preventDefault();
    const allErrs = validateAll(form);
    setErrors(allErrs); setTouched(Object.fromEntries(Object.keys(RULES).map(k => [k, true])));
    if (Object.keys(allErrs).length) { toast.error("Ada field yang belum sesuai — periksa tanda merah"); return; }
    setBusy(true);
    try {
      await api.post("/auth/register", { ...form, business_category: "" });
      await refresh();
      toast.success("Pendaftaran berhasil! Cek email untuk kode verifikasi.");
      nav("/verify-email");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal mendaftar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-bizma-cream py-10 px-4" data-testid="register-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6"><ArrowLeft size={16}/> Kembali</Link>
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo-bizma.png" alt="BIZMA" className="w-11 h-11 rounded-full object-cover shadow-sm" />
          <div>
            <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">BIZMA — Daftar</div>
            <h1 className="text-3xl font-display font-bold">Bergabung ke Komunitas</h1>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="register-form">
          <section className="felt-card p-6 sm:p-8 space-y-4">
            <div className="space-y-1">
              <h2 className="font-display font-semibold text-lg">Biodata Diri</h2>
              <p className="text-sm text-bizma-subtext">Silahkan isi biodata diri Anda.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <PhotoField label="Foto Profil" value={form.photo_path} onChange={(p) => setField("photo_path", p)} onUpload={uploadPublic} testId="reg-photo" required error={err("photo_path")} />
              <div className="grid gap-4">
                <F label="Nama Lengkap" required error={err("name")}>
                  <input value={form.name} onChange={(e) => setField("name", e.target.value)} onBlur={() => blur("name")} className={cn(err("name"))} data-testid="reg-name" />
                </F>
                <F label="Username" hint="Opsional. 3-30, huruf kecil, angka, . _" error={err("username")}>
                  <input value={form.username} onChange={(e) => setField("username", e.target.value)} onBlur={() => blur("username")} className={cn(err("username"))} data-testid="reg-username" />
                </F>
              </div>
            </div>
            <F label="Email" required error={err("email")}>
              <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} onBlur={() => blur("email")} className={cn(err("email"))} data-testid="reg-email" />
            </F>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Password (min 8 karakter)" required error={err("password")}>
                <PasswordInput value={form.password} onChange={(e) => setField("password", e.target.value)} testId="reg-password" autoComplete="new-password" />
              </F>
              <F label="Konfirmasi Password" required error={err("confirm")}>
                <PasswordInput value={form.confirm} onChange={(e) => setField("confirm", e.target.value)} testId="reg-confirm" autoComplete="new-password" />
              </F>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="WhatsApp" hint="Boleh awalan 0 atau 62, sistem akan menormalkan" required error={err("whatsapp")}>
                <input value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} onBlur={() => blur("whatsapp")} inputMode="tel" placeholder="0812xxxxxxx" className={cn(err("whatsapp"))} data-testid="reg-wa" />
              </F>
              <F label="Kecamatan" required>
                <select value={form.kecamatan} onChange={(e) => setField("kecamatan", e.target.value)} className="input-bizma" data-testid="reg-kec">
                  {KECAMATAN_MAGELANG.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </F>
            </div>
            <F label="Alamat Rumah" required error={err("address")}>
              <input value={form.address} onChange={(e) => setField("address", e.target.value)} onBlur={() => blur("address")} className={cn(err("address"))} data-testid="reg-address" />
            </F>
          </section>

          <section className="felt-card p-6 sm:p-8 space-y-4">
            <div className="space-y-1">
              <h2 className="font-display font-semibold text-lg">Data Bisnis</h2>
              <p className="text-sm text-bizma-subtext">Silahkan isi data bisnis Anda.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <PhotoField label="Foto Usaha" value={form.business_photo_path} onChange={(p) => setField("business_photo_path", p)} onUpload={uploadPublic} testId="reg-biz-photo" required error={err("business_photo_path")} aspect="video" />
              <div className="grid gap-4">
                <F label="Nama Usaha" required error={err("business_name")}>
                  <input value={form.business_name} onChange={(e) => setField("business_name", e.target.value)} onBlur={() => blur("business_name")} className={cn(err("business_name"))} data-testid="reg-biz-name" />
                </F>
                <F label="Alamat Usaha" required error={err("business_address")}>
                  <input value={form.business_address} onChange={(e) => setField("business_address", e.target.value)} onBlur={() => blur("business_address")} className={cn(err("business_address"))} data-testid="reg-biz-address" />
                </F>
              </div>
            </div>
            <F label="Deskripsi Usaha" required error={err("business_description")}>
              <textarea rows={3} value={form.business_description} onChange={(e) => setField("business_description", e.target.value)} onBlur={() => blur("business_description")} className={`${cn(err("business_description"))} h-auto py-3`} data-testid="reg-biz-desc" />
            </F>
          </section>

          <button disabled={busy} className="btn-primary w-full h-12" data-testid="reg-submit">
            {busy ? "Mendaftar..." : "Daftar Sekarang"}
          </button>
          <div className="text-center text-sm text-bizma-subtext">Sudah punya akun? <Link to="/login" className="text-bizma-terracotta font-medium">Masuk</Link></div>
        </form>
      </div>
    </div>
  );
}

function F({ label, hint, required, error, children }) {
  return (
    <div>
      <label className="text-sm font-medium">{label} {required && <span className="text-bizma-terracotta">*</span>}</label>
      <div className="mt-2">{children}</div>
      {hint && !error && <div className="text-xs text-bizma-subtext mt-1">{hint}</div>}
      {error && <div className="text-xs text-red-500 mt-1" data-testid="field-error">{error}</div>}
    </div>
  );
}

const cn = (hasErr) => `input-bizma ${hasErr ? "border-red-400 focus:border-red-500" : ""}`;

function PhotoField({ label, value, onChange, onUpload, testId, required, error, aspect = "circle" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const size = aspect === "video" ? "aspect-video w-full max-w-xs" : "w-28 h-28 rounded-full";
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const path = await onUpload(f); onChange(path); }
    catch { toast.error("Gagal unggah"); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-sm font-medium">{label} {required && <span className="text-bizma-terracotta">*</span>}</label>
      <div className="mt-2">
        <button type="button" onClick={() => inputRef.current?.click()} className={`${size} overflow-hidden border-2 border-dashed border-bizma-border grid place-items-center bg-bizma-muted hover:border-bizma-terracotta transition-colors`} data-testid={`${testId}-btn`}>
          {busy ? <Loader2 className="animate-spin text-bizma-subtext"/> : value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover"/> : <div className="text-bizma-subtext flex flex-col items-center gap-1"><Camera size={20}/><span className="text-xs">Unggah</span></div>}
        </button>
        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testId}-input`}/>
      </div>
      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
    </div>
  );
}
